/**
 * Tailwind CSS implementation of Card component.
 * Uses Seeds card CSS classes from card.css.
 */

import React from "react";
import { mergeRefs } from "@sproutsocial/seeds-react-utilities";
import type { TypeCardProps } from "./CardTypes";
import { useCardBehavior } from "./useCardBehavior";
import { SelectedIcon } from "./subComponents";

// Utility for merging class names properly. Mirrored inline from Avatar —
// @sproutsocial/seeds-react-utilities does NOT export a `cn` helper.
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const CardTailwind = React.forwardRef<HTMLDivElement, TypeCardProps>(
  (
    {
      children,
      className,
      disabled = false,
      elevation = "low",
      href,
      onClick,
      role = "presentation",
      selected,
      ...rest
    }: TypeCardProps,
    ref
  ) => {
    const {
      containerRef,
      cardContext,
      hasSubComponent,
      isRoleLink,
      SubComponentContext,
      containerProps,
    } = useCardBehavior({ disabled, href, onClick, role, selected });

    const classes = cn(
      "seeds-card",
      {
        "seeds-card-interactive":
          isRoleLink || role === "button" || role === "checkbox",
        "seeds-card-link-role": isRoleLink,
      },
      {
        "seeds-card-selected": !!selected,
        "seeds-card-disabled": disabled,
        "seeds-card-compositional": hasSubComponent,
      },
      className
    );

    return (
      <div
        {...containerProps}
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore legacy ref type mismatch between the RefObject and callback ref
        ref={mergeRefs(containerRef, ref)}
        className={classes}
        // Per-elevation hover shadow is pushed through a CSS custom property
        // consumed by card.css. Placed after {...rest} so it can't be clobbered.
        style={
          {
            "--seeds-card-hover-shadow": `var(--shadow-${elevation})`,
          } as React.CSSProperties
        }
      >
        <SelectedIcon $selected={selected} />
        <SubComponentContext.Provider value={cardContext}>
          {children}
        </SubComponentContext.Provider>
      </div>
    );
  }
);

CardTailwind.displayName = "CardTailwind";
